import React, { useState } from "react";
import { Eye, EyeOff, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import { useCustomerAuth, customerFetch } from "./CustomerAuthContext";

export default function CustomerProfile() {
  const { customer, token } = useCustomerAuth();
  const [showPw, setShowPw] = useState({ cur: false, next: false, confirm: false });
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  if (!customer) return null;
  const rows = [
    ["Customer Name", customer.name], ["Customer Code", customer.code], ["Email", customer.email],
    ["GSTIN", customer.gstin], ["PAN", customer.pan], ["Contact Person", customer.contact_person],
    ["Phone", customer.phone], ["Address", customer.address], ["City", customer.city],
    ["State", customer.state], ["Pincode", customer.pincode],
  ];
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitPassword = async (e) => {
    e.preventDefault(); setMsg(null);
    if (!form.current_password || !form.new_password || !form.confirm_password) return setMsg({ type: "error", text: "Please fill all password fields." });
    if (form.new_password.length < 8) return setMsg({ type: "error", text: "New password must be at least 8 characters." });
    if (form.new_password !== form.confirm_password) return setMsg({ type: "error", text: "New password and confirmation do not match." });
    if (form.new_password === form.current_password) return setMsg({ type: "error", text: "New password must differ from current password." });
    setSubmitting(true);
    try {
      const r = await customerFetch(token)("/api/customer/auth/change-password", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: form.current_password, new_password: form.new_password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Failed to update password");
      setMsg({ type: "success", text: "Password updated successfully." });
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setMsg({ type: "error", text: err.message || "Something went wrong." });
    } finally { setSubmitting(false); }
  };

  const pwInput = (name, label, visibleKey) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="relative">
        <input type={showPw[visibleKey] ? "text" : "password"} value={form[name]} onChange={onChange(name)}
          data-testid={`customer-${name.replace(/_/g, "-")}-input`}
          autoComplete={name === "current_password" ? "current-password" : "new-password"}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2a5e]/20 focus:border-[#0a2a5e]"
          placeholder="••••••••" />
        <button type="button" onClick={() => setShowPw((s) => ({ ...s, [visibleKey]: !s[visibleKey] }))}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1" tabIndex={-1}>
          {showPw[visibleKey] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="customer-profile">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-[#0a2a5e]">Profile</h1>
        <p className="text-sm text-slate-500 mt-1">Your account details. Contact your BAISHALII sales manager to update.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 divide-y divide-slate-100">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-3 first:pt-0 last:pb-0">
            <div className="text-sm text-slate-500 font-medium">{k}</div>
            <div className="sm:col-span-2 text-sm text-slate-800">{v || <span className="text-slate-400">—</span>}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="customer-change-password-card">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-4 w-4 text-[#0a2a5e]" />
          <h2 className="text-base font-bold text-[#0a2a5e]">Change Login Password</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Use a strong password of at least 8 characters.</p>
        <form onSubmit={submitPassword} className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
          {pwInput("current_password", "Current Password", "cur")}
          {pwInput("new_password", "New Password", "next")}
          {pwInput("confirm_password", "Confirm New Password", "confirm")}
          {msg && (
            <div className={`md:col-span-3 flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${msg.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
              {msg.type === "success" ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <span>{msg.text}</span>
            </div>
          )}
          <div className="md:col-span-3 flex justify-end">
            <button type="submit" disabled={submitting} data-testid="customer-change-password-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-[#0a2a5e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2a5e]/90 disabled:opacity-60">
              {submitting ? "Updating…" : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
